// What Google's reads carry for their widgets (google.tsx), read safely: who is
// on the site now, and Search performance's four numbers and days. Pure, so
// tests can check them without a browser.

import type { NumberFormat, Point, Stream } from "../../shared/types";
import { DAY } from "../../shared/period";

/** How long a count of who is on the site now still says "now": a read older than this is not shown. */
export const LIVE_MS = 10 * 60_000;

/** Active users now, as the source last read them, while that read is recent; otherwise none. */
export function liveOf(s: Pick<Stream, "json"> | null, now = Date.now()): { users: number; minutes: number; at: number } | null {
  const n = (s?.json as { now?: { users?: unknown; minutes?: unknown; at?: unknown } } | null)?.now;
  if (!n || typeof n.users !== "number" || !Number.isFinite(n.users) || typeof n.at !== "number" || typeof n.minutes !== "number") return null;
  return now - n.at < LIVE_MS && n.at <= now + 60_000 ? { users: n.users, minutes: n.minutes, at: n.at } : null;
}

export type Key = "clicks" | "impressions" | "ctr" | "position";
export const KEYS: Key[] = ["clicks", "impressions", "ctr", "position"];
export const NAME: Record<Key, string> = { clicks: "Clicks", impressions: "Impressions", ctr: "CTR", position: "Position" };
export const FORMAT: Record<Key, NumberFormat> = { clicks: { style: "number" }, impressions: { style: "number" }, ctr: { style: "percent", decimals: 1 }, position: { style: "number", decimals: 1 } };
export const TONE: Record<Key, number> = { clicks: 1, impressions: 2, ctr: 3, position: 4 };

export interface Perf {
  all: Record<Key, { total: number; previous: number | null }>;
  days: { t: number; v: Record<Key, number> }[];
}

/** Search Console's four numbers and its days, as the read for Search performance keeps them; null for any other stream. */
export function perfOf(s: Pick<Stream, "json"> | null): Perf | null {
  const j = s?.json as { all?: Record<string, { total?: unknown; previous?: unknown }>; days?: unknown } | null;
  if (!j?.all || !Array.isArray(j.days)) return null;
  const all = {} as Perf["all"];
  for (const k of KEYS) {
    const a = j.all[k];
    if (!a || typeof a.total !== "number" || !Number.isFinite(a.total)) return null;
    all[k] = { total: a.total, previous: typeof a.previous === "number" && Number.isFinite(a.previous) ? a.previous : null };
  }
  const days: Perf["days"] = [];
  for (const raw of j.days as Record<string, unknown>[]) {
    const t = typeof raw?.date === "string" ? Date.parse(raw.date + "T00:00:00Z") : NaN;
    if (!Number.isFinite(t) || KEYS.some((k) => typeof raw[k] !== "number")) continue;
    days.push({ t, v: { clicks: raw.clicks as number, impressions: raw.impressions as number, ctr: (raw.ctr as number) * 100, position: raw.position as number } });
  }
  days.sort((a, b) => a.t - b.t);
  return { all, days };
}


/**
 * The period before, as Google gave its days, moved onto this period's days so the two lie over each other: day one
 * of the period before under day one of this one. Only the days this period shows; none for all time.
 */
export function beforeOf(s: Pick<Stream, "json" | "period" | "points">): Point[] | null {
  const raw = (s.json as { before?: unknown } | null)?.before;
  const days = s.period?.days ?? 0;
  if (!Array.isArray(raw) || !(days > 0 && days < 3650) || s.points.length < 2) return null;
  const first = s.points[0].t;
  const last = s.points[s.points.length - 1].t;
  const out = (raw as { t?: unknown; v?: unknown }[])
    .filter((p) => typeof p?.t === "number" && typeof p.v === "number" && Number.isFinite(p.v))
    .map((p) => ({ t: (p.t as number) + days * DAY, v: p.v as number }))
    .filter((p) => p.t >= first && p.t <= last);
  return out.length > 1 ? out : null;
}
